import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  levels,
  notebookSections,
  schoolIntroHtml,
  MARKET_LABELS,
  type CheckResult,
  type Lesson,
  type LessonStatus,
  type ResultGroup,
} from '../research/content';

const STATUS_BADGE: Record<LessonStatus, string> = {
  Proven: 'badge--green',
  Mastered: 'badge--green',
  'Mastered (weak)': 'badge--green',
  'Partly true': 'badge--yellow',
  'Varies by market': 'badge--yellow',
  'Not proven': 'badge--gray',
  Rejected: 'badge--red',
  'Not run': 'badge--gray',
};

/** Verdict filters: each groups the statuses a reader thinks of as one. */
const FILTERS: { key: string; label: string; statuses: LessonStatus[] }[] = [
  { key: 'all', label: 'All', statuses: [] },
  { key: 'proven', label: 'Proven', statuses: ['Proven', 'Mastered', 'Mastered (weak)'] },
  { key: 'partly', label: 'Partly true', statuses: ['Partly true', 'Varies by market'] },
  { key: 'rejected', label: 'Rejected', statuses: ['Rejected'] },
  { key: 'unproven', label: 'Not proven', statuses: ['Not proven', 'Not run'] },
];

const VERDICT_MARK: Record<string, { mark: string; cls: string }> = {
  holds: { mark: '✓', cls: 'check-pass' },
  fails: { mark: '✗', cls: 'check-fail' },
  uncertain: { mark: '?', cls: 'check-unsure' },
};

function pct(x: number): string {
  return `${x > 0 ? '+' : ''}${x.toFixed(1)}%`;
}

function CheckLine({ c }: { c: CheckResult }) {
  // Older results have no verdict: show the plain pass/fail.
  const v = VERDICT_MARK[c.verdict ?? (c.passed ? 'holds' : 'fails')]!;
  const evidence: string[] = [`${c.leftValue} ${c.op} ${c.rightValue}`];
  if (c.confidence !== undefined) {
    evidence.push(c.confidence === null ? 'not resampled' : `holds in ${Math.round(c.confidence * 100)}% of resamples`);
  }
  if (c.periods?.length) {
    evidence.push(`periods ${c.periods.map((p) => (p.passed === null ? '–' : p.passed ? '✓' : '✗')).join(' ')}`);
  }
  return (
    <li className={v.cls}>
      <span className="school-check-mark">{v.mark}</span>
      <span>
        {c.text}
        <span className="school-evidence">{evidence.join(' · ')}</span>
      </span>
    </li>
  );
}

function GroupResults({ market, group }: { market: string; group: ResultGroup }) {
  const label = MARKET_LABELS[market] ?? market;
  const rows = Object.entries(group.experiments);
  const hasAvgMonth = rows.some(([, m]) => m.avgMonthPct !== undefined);
  return (
    <div className="research-group">
      {label && (
        <div className="research-card-head">
          <strong>{label}</strong>
          {group.verdict && (
            <span className={`badge ${STATUS_BADGE[group.verdict as LessonStatus] ?? 'badge--gray'}`}>{group.verdict}</span>
          )}
        </div>
      )}
      <ul className="research-checks school-checks">
        {group.checks.map((c) => (
          <CheckLine key={c.text} c={c} />
        ))}
      </ul>
      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>Experiment</th>
              <th>Return</th>
              {hasAvgMonth && <th>Average month</th>}
              <th>Worst fall</th>
              <th>Winning months</th>
              <th>Trades</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(([name, m]) => (
              <tr key={name}>
                <td className="cell-mono">{name.replace(/@[a-z]+$/, '')}</td>
                <td>{pct(m.totalReturnPct)}</td>
                {hasAvgMonth && <td>{m.avgMonthPct === undefined ? '–' : pct(m.avgMonthPct)}</td>}
                <td>{m.maxDrawdownPct.toFixed(1)}%</td>
                <td>{m.profitableMonthsPct.toFixed(0)}%</td>
                <td>{m.trades}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function LessonRow({ lesson, open, onToggle }: { lesson: Lesson; open: boolean; onToggle: () => void }) {
  const r = lesson.results;
  const warnings = r?.engineWarnings ? Object.entries(r.engineWarnings) : [];
  return (
    <div className={`school-lesson${open ? ' school-lesson--open' : ''}`} id={lesson.id}>
      <button type="button" className="school-lesson-head" onClick={onToggle} aria-expanded={open}>
        <span className="school-chevron" aria-hidden="true">
          {open ? '▾' : '▸'}
        </span>
        <span className="cell-mono school-lesson-id">{lesson.id}</span>
        <span className="school-lesson-title">{lesson.title}</span>
        <span className={`badge ${STATUS_BADGE[lesson.status]}`}>{lesson.status}</span>
      </button>
      {open && (
        <div className="school-lesson-body">
          <p className="school-label">The claim</p>
          <p className="research-claim">{lesson.claim}</p>
          <p className="section-description">
            {r?.preregistered
              ? `Claim fixed before the test (commit ${r.preregistered.commit.slice(0, 7)})${r.runAt ? `; run ${r.runAt.slice(0, 10)}` : ''}.`
              : r?.draft
                ? "Draft run: the claim wasn't fixed before the test."
                : r
                  ? 'Graded before pre-registration (older lesson).'
                  : 'Not run yet.'}
          </p>
          {warnings.length > 0 && (
            <div className="school-warning">
              <strong>Engine warnings in this run:</strong>
              <ul>
                {warnings.flatMap(([exp, kinds]) =>
                  Object.entries(kinds).map(([kind, n]) => (
                    <li key={exp + kind}>
                      <span className="cell-mono">{exp}</span>: {kind}
                      {n > 1 ? ` (×${n})` : ''}
                    </li>
                  )),
                )}
              </ul>
            </div>
          )}
          {r &&
            Object.entries(r.groups).map(([market, group]) => <GroupResults key={market} market={market} group={group} />)}
          {lesson.html && (
            <details className="research-details">
              <summary>Read the full lesson</summary>
              <div className="markdown" dangerouslySetInnerHTML={{ __html: lesson.html }} />
            </details>
          )}
        </div>
      )}
    </div>
  );
}

/** Small coloured counts of a level's verdicts, for the level list. */
function VerdictDots({ lessons }: { lessons: Lesson[] }) {
  const n = (s: LessonStatus[]) => lessons.filter((l) => s.includes(l.status)).length;
  const parts: [number, string][] = [
    [n(FILTERS[1]!.statuses), 'school-dot--green'],
    [n(FILTERS[2]!.statuses), 'school-dot--yellow'],
    [n(FILTERS[3]!.statuses), 'school-dot--red'],
    [n(FILTERS[4]!.statuses), 'school-dot--gray'],
  ];
  return (
    <span className="school-dots">
      {parts
        .filter(([count]) => count > 0)
        .map(([count, cls]) => (
          <span key={cls} className={`school-dot ${cls}`}>
            {count}
          </span>
        ))}
    </span>
  );
}

function matches(lesson: Lesson, q: string): boolean {
  if (!q) return true;
  return `${lesson.id} ${lesson.title} ${lesson.claim}`.toLowerCase().includes(q);
}

function LessonsTab() {
  const location = useLocation();
  const navigate = useNavigate();
  const newest = levels[levels.length - 1]?.number ?? 1;
  const [levelNo, setLevelNo] = useState<number>(newest);
  const [filter, setFilter] = useState('all');
  const [query, setQuery] = useState('');
  const [openIds, setOpenIds] = useState<Set<string>>(new Set());

  // A link like /research/school#L24-01 opens that lesson in its level.
  useEffect(() => {
    const id = location.hash.slice(1);
    if (!id) return;
    const level = levels.find((l) => l.lessons.some((x) => x.id === id));
    if (!level) return;
    setLevelNo(level.number);
    setQuery('');
    setOpenIds((s) => new Set(s).add(id));
    requestAnimationFrame(() => document.getElementById(id)?.scrollIntoView({ block: 'start' }));
  }, [location.hash]);

  const q = query.trim().toLowerCase();
  const statuses = FILTERS.find((f) => f.key === filter)!.statuses;
  const keep = (l: Lesson) => (statuses.length === 0 || statuses.includes(l.status)) && matches(l, q);
  // Searching or filtering looks across every level; otherwise show the chosen level.
  const searching = q !== '' || filter !== 'all';
  const shown = searching
    ? levels.map((l) => ({ level: l, lessons: l.lessons.filter(keep) })).filter((x) => x.lessons.length > 0)
    : levels.filter((l) => l.number === levelNo).map((l) => ({ level: l, lessons: l.lessons }));
  const all = levels.flatMap((l) => l.lessons);
  const total = shown.reduce((a, x) => a + x.lessons.length, 0);

  const toggle = (id: string) =>
    setOpenIds((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const pickLevel = (n: number) => {
    setLevelNo(n);
    setQuery('');
    setFilter('all');
    if (location.hash) navigate(location.pathname, { replace: true });
  };

  return (
    <div className="school-layout">
      <nav className="school-levels" aria-label="Levels">
        <select
          className="school-level-select"
          value={levelNo}
          onChange={(e) => pickLevel(Number(e.target.value))}
          aria-label="Choose a level"
        >
          {levels.map((l) => (
            <option key={l.number} value={l.number}>
              {l.title}
            </option>
          ))}
        </select>
        <ul className="school-level-list">
          {[...levels].reverse().map((l) => (
            <li key={l.number}>
              <button
                type="button"
                className={`school-level-btn${!searching && l.number === levelNo ? ' school-level-btn--active' : ''}`}
                onClick={() => pickLevel(l.number)}
              >
                <span className="school-level-name">{l.title}</span>
                <VerdictDots lessons={l.lessons} />
              </button>
            </li>
          ))}
        </ul>
      </nav>

      <div className="school-main">
        <div className="school-toolbar">
          <input
            type="search"
            className="school-search"
            placeholder="Search lessons (e.g. bitcoin, stop, meme)"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <div className="school-filters" role="group" aria-label="Filter by verdict">
            {FILTERS.map((f) => (
              <button
                key={f.key}
                type="button"
                className={`school-chip${filter === f.key ? ' school-chip--active' : ''}`}
                onClick={() => setFilter(f.key)}
              >
                {f.label}
                <span className="school-chip-count">
                  {f.statuses.length === 0 ? all.length : all.filter((l) => f.statuses.includes(l.status)).length}
                </span>
              </button>
            ))}
          </div>
        </div>

        {searching && (
          <p className="section-description">
            {total} lesson{total === 1 ? '' : 's'} across all levels.{' '}
            <button
              type="button"
              className="school-link-btn"
              onClick={() => {
                setQuery('');
                setFilter('all');
              }}
            >
              Clear
            </button>
          </p>
        )}
        {shown.length === 0 && <p className="section-description">No lessons match.</p>}

        {shown.map(({ level, lessons }) => (
          <section key={level.number} className="school-level">
            <h3>{level.title}</h3>
            {!searching && level.lessons.length > 1 && (
              <p className="section-description">
                <button type="button" className="school-link-btn" onClick={() => setOpenIds(new Set(level.lessons.map((l) => l.id)))}>
                  Open all
                </button>
                {' · '}
                <button type="button" className="school-link-btn" onClick={() => setOpenIds(new Set())}>
                  Close all
                </button>
              </p>
            )}
            <div className="card school-lessons">
              {lessons.map((lesson) => (
                <LessonRow key={lesson.id} lesson={lesson} open={openIds.has(lesson.id)} onToggle={() => toggle(lesson.id)} />
              ))}
            </div>
            {!searching &&
              level.notes.map((note) => (
                <details key={note.title} className="card research-details">
                  <summary>{note.title}</summary>
                  <div className="markdown" dangerouslySetInnerHTML={{ __html: note.html }} />
                </details>
              ))}
          </section>
        ))}
      </div>
    </div>
  );
}

function NotebookTab() {
  const [query, setQuery] = useState('');
  const [section, setSection] = useState(0);
  const q = query.trim().toLowerCase();
  const hits = useMemo(
    () => notebookSections.map((s, i) => ({ s, i })).filter(({ s }) => !q || s.text.includes(q) || s.title.toLowerCase().includes(q)),
    [q],
  );
  const shown = q ? hits : hits.filter(({ i }) => i === section);

  return (
    <div className="school-layout">
      <nav className="school-levels" aria-label="Notebook sections">
        <select
          className="school-level-select"
          value={section}
          onChange={(e) => {
            setSection(Number(e.target.value));
            setQuery('');
          }}
          aria-label="Choose a section"
        >
          {notebookSections.map((s, i) => (
            <option key={s.title} value={i}>
              {s.title}
            </option>
          ))}
        </select>
        <ul className="school-level-list">
          {notebookSections.map((s, i) => (
            <li key={s.title}>
              <button
                type="button"
                className={`school-level-btn${!q && i === section ? ' school-level-btn--active' : ''}`}
                onClick={() => {
                  setSection(i);
                  setQuery('');
                }}
              >
                <span className="school-level-name">{s.title}</span>
              </button>
            </li>
          ))}
        </ul>
      </nav>
      <div className="school-main">
        <div className="school-toolbar">
          <input
            type="search"
            className="school-search"
            placeholder="Search the rules (e.g. bitcoin, stop loss, gold)"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
        {q && (
          <p className="section-description">
            {hits.length} section{hits.length === 1 ? '' : 's'} mention “{query.trim()}”.
          </p>
        )}
        {shown.map(({ s }) => (
          <section key={s.title} className="card school-notebook-section">
            <h3>{s.title}</h3>
            <div className="markdown" dangerouslySetInnerHTML={{ __html: s.html }} />
          </section>
        ))}
      </div>
    </div>
  );
}

export function SchoolPage() {
  const location = useLocation();
  const [tab, setTab] = useState<'lessons' | 'notebook'>('lessons');
  const all = levels.flatMap((l) => l.lessons);
  const count = (s: LessonStatus[]) => all.filter((l) => s.includes(l.status)).length;

  // A lesson link always lands on the lessons tab.
  useEffect(() => {
    if (location.hash) setTab('lessons');
  }, [location.hash]);

  return (
    <div className="page">
      <h2>Trading School</h2>
      <details className="school-intro">
        <summary>How the school works</summary>
        <div className="markdown" dangerouslySetInnerHTML={{ __html: schoolIntroHtml }} />
      </details>

      <div className="school-summary">
        <span>
          <strong>{all.length}</strong> lessons in <strong>{levels.length}</strong> levels
        </span>
        <span className="school-dot school-dot--green">{count(FILTERS[1]!.statuses)} proven</span>
        <span className="school-dot school-dot--yellow">{count(FILTERS[2]!.statuses)} partly true</span>
        <span className="school-dot school-dot--red">{count(FILTERS[3]!.statuses)} rejected</span>
        <span className="school-dot school-dot--gray">{count(FILTERS[4]!.statuses)} not proven</span>
      </div>

      <div className="school-tabs" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'lessons'}
          className={`school-tab${tab === 'lessons' ? ' school-tab--active' : ''}`}
          onClick={() => setTab('lessons')}
        >
          Lessons
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'notebook'}
          className={`school-tab${tab === 'notebook' ? ' school-tab--active' : ''}`}
          onClick={() => setTab('notebook')}
        >
          The manager's notebook
        </button>
      </div>

      {tab === 'lessons' ? <LessonsTab /> : <NotebookTab />}
    </div>
  );
}
